import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// Types
export interface LLMProvider {
  id: string;
  name: 'anthropic' | 'openai' | 'gemini';
  display_name: string;
  model_id: string;
  is_active: boolean;
  is_primary: boolean;
  config?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
  };
}

export interface CreateProviderRequest {
  name: 'anthropic' | 'openai' | 'gemini';
  display_name: string;
  api_key: string;
  model_id: string;
  config?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
  };
  is_primary?: boolean;
}

export interface UpdateProviderRequest {
  display_name?: string;
  api_key?: string;
  model_id?: string;
  config?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
  };
  is_active?: boolean;
  is_primary?: boolean;
}

export interface GenerationJob {
  id: string;
  type: string;
  job_id?: string;
  artifact_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: string;
  error?: string;
  token_usage?: {
    input: number;
    output: number;
    total: number;
  };
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface GenerateArtifactRequest {
  job_id?: string;
  artifact_type: string;
  content_ids?: string[];
  job_description: string;
  user_content: string;
  job_title: string;
  company: string;
  provider_id: string;
  prompt_template_id?: string;
  artifact_id?: string; // Optional: ID of placeholder artifact to update instead of creating new
}


// ==================== PROVIDER QUERIES ====================

/**
 * Get all LLM providers
 */
export const useProviders = () => {
  return useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers`, { headers });
      if (!res.ok) {
        throw new Error('Failed to fetch providers');
      }
      const data = await res.json();
      return data.data as LLMProvider[];
    },
  });
};

/**
 * Get a specific provider
 */
export const useProvider = (id: string) => {
  return useQuery({
    queryKey: ['providers', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers/${id}`, { headers });
      if (!res.ok) {
        throw new Error('Failed to fetch provider');
      }
      const data = await res.json();
      return data.data as LLMProvider;
    },
    enabled: !!id,
  });
};

// ==================== PROVIDER MUTATIONS ====================

/**
 * Create a new provider
 */
export const useCreateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateProviderRequest) => {
      console.log('[LLM] Creating provider with request:', request);

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      console.log('[LLM] Create provider response status:', res.status);

      if (!res.ok) {
        const error = await res.json();
        console.error('[LLM] Create provider error:', error);
        throw new Error(error.error || 'Failed to create provider');
      }

      const data = await res.json();
      console.log('[LLM] Create provider success:', data);
      return data.data as LLMProvider;
    },
    onSuccess: () => {
      console.log('[LLM] Invalidating providers query');
      queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
    onError: (error) => {
      console.error('[LLM] Mutation error:', error);
    },
  });
};

/**
 * Update a provider
 */
export const useUpdateProvider = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateProviderRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(request),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update provider');
      }

      const data = await res.json();
      return data.data as LLMProvider;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['providers', id] });
    },
  });
};

/**
 * Delete a provider
 */
export const useDeleteProvider = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete provider');
      }

      const data = await res.json();
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
  });
};

/**
 * Test provider connection
 */
export const useTestProvider = (id: string) => {
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers/${id}/test`, {
        method: 'POST',
        headers,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Connection test failed');
      }

      const data = await res.json();
      return data.data;
    },
  });
};

/**
 * Set a provider as primary
 */
export const useSetPrimaryProvider = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/providers/${id}/primary`, {
        method: 'PUT',
        headers,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to set primary provider');
      }

      const data = await res.json();
      return data.data as LLMProvider;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
    },
  });
};

// ==================== GENERATION QUERIES ====================

/**
 * Get generation job status
 */
export const useGenerationStatus = (generationJobId: string | null) => {
  return useQuery({
    queryKey: ['generation', generationJobId],
    queryFn: async () => {
      if (!generationJobId) return null;

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/generation/${generationJobId}`, { headers });
      if (!res.ok) {
        throw new Error('Failed to fetch generation status');
      }
      const data = await res.json();
      return data.data as GenerationJob;
    },
    // Poll every 500ms while pending or processing
    refetchInterval: (query) => {
      const data = query.state.data as GenerationJob | null;
      // Poll if we don't have data yet, OR if status is pending/processing
      if (!data) return 500; // Keep polling until we get data
      return data.status === 'pending' || data.status === 'processing' ? 500 : false;
    },
    enabled: !!generationJobId,
  });
};

// ==================== GENERATION MUTATIONS ====================

/**
 * Start artifact generation
 */
export const useGenerateArtifact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: GenerateArtifactRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to start generation');
      }

      const data = await res.json();
      return data.data as GenerationJob;
    },
    onSuccess: () => {
      // Invalidate artifacts on generation success
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
  });
};

/**
 * Retry a failed generation
 */
export const useRetryGeneration = (generationJobId: string) => {
  return useMutation({
    mutationFn: async (providerId?: string) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/generation/${generationJobId}/retry`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ provider_id: providerId }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to retry generation');
      }

      const data = await res.json();
      return data.data as GenerationJob;
    },
  });
};

/**
 * Get debug info for a generation job (includes compiled prompt)
 */
export interface GenerationDebugInfo {
  generationId: string;
  status: string;
  artifactType: string;
  jobId?: string;
  promptTemplateId?: string;
  providerId: string;
  compiledPrompt: string;
  requestBody: {
    jobDescription: string;
    userContent: string;
    jobTitle: string;
    company: string;
  };
  result?: string;
  error?: string;
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  timestamps: {
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
  };
}

export const useGenerationDebugInfo = (generationJobId: string | null) => {
  return useQuery({
    queryKey: ['generation-debug', generationJobId],
    queryFn: async () => {
      if (!generationJobId) return null;

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/generation/debug/${generationJobId}`, { headers });
      if (!res.ok) {
        throw new Error('Failed to fetch debug info');
      }
      const data = await res.json();
      return data.data as GenerationDebugInfo;
    },
    enabled: !!generationJobId,
  });
};
